import React, { useContext, useEffect, useState } from "react";
import { getAppConfig } from "@config/appConfig";
import { PLACEHOLDER_MOVIE_POSTER } from "@config/ImageSizes";
import { ApplicationContext } from "@context/ApplicationContext";
import { SearchContext } from "@context/SearchContext";
import PlayIcon from "@components/Icons/PlayIcon";
import TrailerButton from "@components/TrailerButton";
import AddToWatchListButton from "@components/V3/AddToWatchListButton";
import { getImageAtSize } from "@utils/imageUtils";
import "./style.scss";

const getRelativeLuminance = (r, g, b) => {
    const [rs, gs, bs] = [r, g, b].map(c => {
        c /= 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055)**2.4;
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};
const MovieTitleCard = () => {
    const { movie } = useContext(SearchContext);
    const { whiteLabel } = useContext(ApplicationContext);
    const config = getAppConfig();
    const fallbackPoster = `${config?.cdnUrl}${PLACEHOLDER_MOVIE_POSTER}`;
    const fallbackImage = `${config?.cdnUrl}asset/img/default-movie-poster-large.webp`;
    const [imageLoaded, setImageLoaded] = useState(false);
    const hideWatchlist = whiteLabel?.hide_navigation_menu === true || whiteLabel?.hide_profile_menu === true;

    const [shouldUseLightColor] = useState(() => {
        const primaryColorValue = getComputedStyle(document.documentElement)
            .getPropertyValue("--chv-color-primary")
            .trim();

        if (!primaryColorValue) return false;

        const [r, g, b] = primaryColorValue.split(",").map(Number);
        const luminance = getRelativeLuminance(r, g, b);
        return luminance < 0.5;
    });


    useEffect(() => {
        setImageLoaded(false);
    }, [movie?.id]);

    if (!movie || !movie.id) {
        return null;
    }

    const imageUrl = movie.assigned_still ?? movie.movie_still ?? movie.images?.[0]?.local_url ?? fallbackImage;

    return (
        <div className="search-details-movie">
            <div className="search-details-movie__info">
                <div className="search-details-movie__poster-wrapper">
                    <picture>
                        <source media="(min-width: 768px)" srcSet={movie.movie_poster || fallbackPoster} />
                        <img
                            className="search-details-movie__poster"
                            src={getImageAtSize(movie.movie_poster, 280) || fallbackPoster}
                            alt={movie?.title}
                        />
                    </picture>
                </div>
                <div className="search-details-movie__text-container">
                    <h1 className="search-details-movie__title">{movie?.title}</h1>
                    <h2 className="search-details-movie__text">{movie?.review?.review_description}</h2>
                    <div className="search-details-movie__buttons">
                        {movie.trailer_url ? (
                            <TrailerButton
                                extraClass="search-details-movie__button"
                                type="mp4"
                                trailerLink={movie.trailer_url}
                                aria-label={`Watch trailer for ${movie.movie_title}`}
                            >
                                <PlayIcon className="search-details-movie__icon-svg" aria-hidden="true" />
                                <span className="search-details-movie__icon-text">Official Trailer</span>
                            </TrailerButton>
                        ) : null}

                        {!hideWatchlist && (
                            <AddToWatchListButton
                                movieId={movie.id}
                                forceLightColor={shouldUseLightColor}
                            />
                        )}
                    </div>
                </div>
            </div>
            <div className="search-details-movie__wrapper">
                <img
                    className="search-details-movie__image"
                    src={imageUrl}
                    alt={movie?.title || "Movie poster"}
                    style={{
                        visibility: imageLoaded ? "visible" : "hidden",
                        opacity: imageLoaded ? 1 : 0
                    }}
                    onError={(e) => {
                        if (e.target.src !== fallbackImage) {
                            e.target.src = fallbackImage;
                        }
                        setImageLoaded(true);
                    }}
                    onLoad={() => {
                        setImageLoaded(true);
                    }}
                />
            </div>
            <span className="search-details-movie__background"></span>
        </div>
    );
};

export default MovieTitleCard;

